/**
 * Runtime API schemas — zod equivalents of `api/types.ts`.
 *
 * Every successful response from the typed HTTP client (`lib/api.ts`) is
 * parsed through one of these. Mismatches throw an ApiError with code
 * CONTRACT_VIOLATION, so contract drift surfaces immediately rather than
 * as a TypeError deep in a render path.
 *
 * These are hand-maintained, and are the mirror of `api/types.ts`. Generating
 * them from the FastAPI OpenAPI spec has been discussed but nothing is wired
 * up for it, so this file is what to edit when a response shape changes.
 */

import { z } from "zod";

// ── Primitives ─────────────────────────────────────────────────────────────

const figureBasisSchema = z.enum([
  "HHV",
  "LHV",
  "MJ",
  "gCO2e_per_MJ",
  "unitless_fraction",
  "unitless_percent",
  "scf",
  "mscf",
  "mmbtu",
  "dth",
  "therm",
  "kwh",
  "lbs",
  "rf_gallon",
  "rin_d3",
  "mt_co2e",
]);

/**
 * Figure with a specific basis. Decimal-as-string per API_CONTRACT §3 —
 * NEVER use Number() on the .value field; pass to decimal.js or the
 * `@compliance/domain/decimal` helpers.
 */
function figureOf<B extends z.ZodLiteral<string>>(basisLiteral: B) {
  return z.object({
    value: z.string(),
    basis: basisLiteral,
  });
}

const anyFigureSchema = z.object({
  value: z.string(),
  basis: figureBasisSchema,
});

// ── Identity ───────────────────────────────────────────────────────────────

export const userRoleSchema = z.enum(["operations", "reviewer", "admin"]);

export const organizationBrandingSchema = z.object({
  custody_party_label: z.string(),
  dispensing_partner_label: z.string(),
});

export const organizationGrantSchema = z.object({
  organization_id: z.string(),
  name: z.string(),
  role: userRoleSchema,
  // Optional, not defaulted, for the same deploy-window reason as
  // `organizations` below: a backend predating the field must still parse.
  branding: organizationBrandingSchema.optional(),
});

export const userResponseSchema = z.object({
  id: z.string(),
  cognito_sub: z.string(),
  email: z.string(),
  // Nullable when the access token carries no name claim: the BE leaves
  // display_name unset until the admin (or the user) edits it. UI must fall
  // back to email when display_name is null.
  display_name: z.string().nullable(),
  role: userRoleSchema,
  status: z.enum(["active", "disabled"]),
  // The organization this SESSION acts in, which is not necessarily the one the
  // identity belongs to: the BE resolves it from the caller's compliance
  // grants, because Microsoft sign-in pins a token's tenant to the identity's
  // home organization while the grant lives in the customer organization the
  // person works in. The BE always sends it.
  organization_id: z.string(),
  // Every organization the caller may act in, this one included — one entry in
  // the ordinary case. Optional, not defaulted: a backend that predates the
  // field must still parse, because the two halves of this change deploy
  // separately and a required field would turn that window into a hard
  // CONTRACT_VIOLATION for every user.
  organizations: z.array(organizationGrantSchema).optional(),
});

// PATCH /users/{user_id}/role returns the compliance product-access row
// (identity model), NOT the /users/me identity envelope: the primary key is
// `user_id` (the identity.users id), the status is the product-access status,
// and it carries the org it was granted under. `status` shares the /me
// active/disabled vocabulary.
export const userAccessViewSchema = z.object({
  user_id: z.string(),
  email: z.string(),
  display_name: z.string().nullable(),
  role: userRoleSchema,
  status: z.enum(["active", "disabled"]),
  organization_id: z.string(),
});

// ── Batches ────────────────────────────────────────────────────────────────
//
// BatchMetadata is the BE's canonical envelope (API_CONTRACT §8). It does
// NOT carry headline figures — those live in GET /batches/{id}/calc-results.
// FE pages that need figures fetch calc-results separately and join by
// batch_id. We keep `period_label` as an optional field on the FE type for
// backward compatibility with screens that compute or don't need it.

export const batchStateSchema = z.enum(["Open", "PendingApproval", "Approved"]);

const batchTotalsSchema = z.object({
  total_hub_scf: figureOf(z.literal("scf")),
  token_mmbtu: figureOf(z.literal("HHV")),
  utility_mmbtu: figureOf(z.literal("HHV")),
  eligible_mmbtu: figureOf(z.literal("HHV")),
  rf_gallons: figureOf(z.literal("rf_gallon")),
  d3_rins: figureOf(z.literal("rin_d3")),
  feedstock_lbs: figureOf(z.literal("lbs")),
});

export const batchSummarySchema = z.object({
  batch_id: z.string(),
  state: batchStateSchema,
  period_start: z.string(),
  period_end: z.string(),
  data_version: z.string(),
  config_version: z.string(),
  // The four per-batch config values frozen on the batch. `z.lazy` defers to
  // `batchConfigSchema` (defined later in this module) to avoid a load-order TDZ.
  batch_config: z
    .lazy(() => batchConfigSchema)
    .nullable()
    .optional(),
  // Optional — not in BatchMetadata; FE derives from period_start or omits.
  period_label: z.string().optional(),
  totals: batchTotalsSchema.optional(),
});

export const batchDetailSchema = batchSummarySchema.extend({
  created_at: z.string(),
  updated_at: z.string(),
});

// ── Corrections (Layer 2) ──────────────────────────────────────────────────
// Mirrors the BE's CorrectionRecord. GET /batches/{id}/corrections returns a
// bare array (no list envelope), so consumers wrap with z.array(...).

export const correctionTypeSchema = z.enum(["substitution", "exclusion"]);
export const correctionStatusSchema = z.enum(["proposed", "approved", "rejected"]);

export const correctionRecordSchema = z.object({
  correction_id: z.string(),
  batch_id: z.string(),
  correction_type: correctionTypeSchema,
  status: correctionStatusSchema,
  source_identifier: z.string().nullable(),
  period_start: z.string().nullable(),
  period_end: z.string().nullable(),
  before_value: z.string().nullable(),
  after_value: z.string().nullable(),
  proposed_by: z.string(),
  proposed_at: z.string(),
  proposal_reason: z.string().nullable(),
  decided_by: z.string().nullable(),
  decided_at: z.string().nullable(),
  decision_note: z.string().nullable(),
  // Actor identities resolved from identity.users (BIO-257). Optional so a
  // pre-BIO-257 backend (FE deployed first) still validates.
  proposed_by_email: z.string().nullable().optional(),
  proposed_by_display_name: z.string().nullable().optional(),
  decided_by_email: z.string().nullable().optional(),
  decided_by_display_name: z.string().nullable().optional(),
  // BIO-308 #5b: whether approving this correction moves the batch's filed
  // figures. Defaulted rather than required so a backend that predates the
  // field still parses — it reads as disclosure-only, which is what every
  // correction was before that change.
  affects_figures: z.boolean().default(false),
});

// ── Issues ─────────────────────────────────────────────────────────────────
// Shape mirrors the BE's IssueRecord per the live OpenAPI (2026-06-05).

export const issueFamilySchema = z.enum([
  "completeness",
  "range",
  "freshness",
  "calibration",
  "balance_closure",
  "upload_schema",
]);

export const issueStatusSchema = z.enum([
  "open",
  "acknowledged",
  "in_progress",
  "resolved",
  "ignored",
]);

export const recommendedActionSchema = z.enum([
  "investigate",
  "substitute",
  "approve_exclusion",
  "recalibrate",
  "reupload",
  "no_action",
]);

export const issueSchema = z.object({
  id: z.string(),
  seq: z.number(),
  severity: z.enum(["critical", "high", "medium", "informational"]),
  family: issueFamilySchema,
  status: issueStatusSchema,
  source_identifier: z.string(),
  batch_id: z.string().nullable(),
  period_start: z.string().nullable(),
  period_end: z.string().nullable(),
  opened_at: z.string(),
  description: z.string(),
  recommended_action: recommendedActionSchema,
  regulatory_basis: z.string().nullable(),
  must_resolve: z.boolean(),
  detail: z.record(z.unknown()).nullable(),
  current_assignee: z.string().nullable(),
  assigned_at: z.string().nullable(),
  acknowledged_at: z.string().nullable(),
  acknowledged_by: z.string().nullable(),
  resolved_at: z.string().nullable(),
  resolved_by: z.string().nullable(),
  resolution_note: z.string().nullable(),
});

// ── Audit ──────────────────────────────────────────────────────────────────

// Mirrors the backend `AuditLogEntry` (compliance/audit/models.py) on the wire.
// FastAPI emits the Pydantic field names verbatim (no aliases), so the raw
// field names must match exactly: { id, seq, ts, user_id, action, entity_type,
// entity_id, batch_id, reason, before, after, software_version, request_id,
// client_ip, actor_email, actor_display_name }. `actor_email` /
// `actor_display_name` are display-only (LEFT JOIN to compliance.users).
//
// We keep the full raw row AND derive the FE display fields (`at`/`actor`) so
// both the raw-shape and display-shape consumers (AuditTab) work without change.
// `actor` falls back display_name → email → user_id → "system". `user_id` is
// nullable (system actions have none) but always present, matching the
// AuditEntry type and the live BE.
export const auditEntrySchema = z
  .object({
    id: z.string(),
    seq: z.number(),
    ts: z.string(),
    user_id: z.string().nullable(),
    action: z.string(),
    entity_type: z.string(),
    entity_id: z.string().nullable(),
    batch_id: z.string().nullable(),
    reason: z.string().nullable(),
    before: z.record(z.unknown()).nullable(),
    after: z.record(z.unknown()).nullable(),
    software_version: z.string(),
    request_id: z.string().nullable(),
    client_ip: z.string().nullable(),
    actor_email: z.string().nullable(),
    actor_display_name: z.string().nullable(),
  })
  .transform((r) => ({
    ...r,
    // Derived display fields layered on top of the raw BE row.
    at: r.ts,
    actor: r.actor_display_name ?? r.actor_email ?? r.user_id ?? "system",
  }));

// ── Reports ────────────────────────────────────────────────────────────────

export const reportSchema = z.object({
  report_id: z.string(),
  batch_id: z.string(),
  report_type: z.string(),
  short_name: z.string(),
  title: z.string(),
  data_version: z.string(),
  software_version: z.string().nullable(),
  generated_at: z.string().nullable(),
  content_length: z.number().nullable(),
});

/** Derive a display title from a CamelCase report short-name (BE rows carry no title). */
function humanizeShortName(short: string): string {
  return short.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");
}

/**
 * Reports-list item. The deployed BE's report-list wire shape
 * (`ReportGenerationRecord`) is leaner than the MSW mock's richer FE shape: it
 * carries `id` and omits `title`, `content_length`, AND `short_name` — the
 * quarterly RFS4800 / yearly RFS0902 list + generate responses send only
 * `report_type` (e.g. "Rfs4800QuarterlyReport"). This schema accepts EITHER and
 * normalizes to the FE `Report` type, so the list parses on both. `short_name`
 * MUST stay optional: when the BE omits it we fall back to `report_type`.
 * Requiring it made `z.array(...).parse()` throw on the deployed payload, which
 * errored the quarterly/yearly reports query and silently hid the Download
 * button after a successful generate.
 */
export const reportListItemSchema = z
  .object({
    id: z.string().optional(),
    report_id: z.string().optional(),
    batch_id: z.string(),
    report_type: z.string(),
    short_name: z.string().optional(),
    title: z.string().optional(),
    data_version: z.string(),
    software_version: z.string().nullable(),
    generated_at: z.string().nullable(),
    content_length: z.number().nullable().optional(),
    // The artifact's real format + name. The deployed BE
    // (`ReportGenerationRecord`) has always sent both; they stayed off this
    // schema, so the UI fell back to guessing "csv" and mislabelled the
    // RFS4800/RFS0902 workbooks (BIO-309). Optional because the MSW mock rows
    // and the lean generate responses don't carry them.
    // nullish, not optional: a row may serialize these as explicit null, and a
    // single rejected row would fail the whole list parse and hide every report
    // for the batch.
    format: z.string().nullish(),
    filename: z.string().nullish(),
  })
  .transform((r) => {
    // BE omits short_name on the lean wire shape — derive it from report_type.
    const shortName = r.short_name ?? r.report_type;
    return {
      report_id: r.report_id ?? r.id ?? "",
      batch_id: r.batch_id,
      report_type: r.report_type,
      short_name: shortName,
      title: r.title ?? humanizeShortName(shortName),
      data_version: r.data_version,
      software_version: r.software_version,
      generated_at: r.generated_at,
      content_length: r.content_length ?? null,
      format: r.format ?? null,
      filename: r.filename ?? null,
    };
  });

/** An artifact's cell grid, for inline review before download (BIO-309). */
export const reportPreviewSchema = z.object({
  report_id: z.string(),
  batch_id: z.string(),
  short_name: z.string(),
  filename: z.string(),
  format: z.string(),
  sheet: z.string().nullable(),
  sheets: z.array(z.string()),
  rows: z.array(z.array(z.string())),
  truncated: z.boolean(),
  total_rows: z.number().nullable(),
  formulas_uncalculated: z.boolean(),
  generated_at: z.string(),
});

/**
 * Lean envelope returned by the BE's POST /batches/{id}/reports/emts/generate.
 * No short_name / title / content_length on the wire — those live in our
 * richer FE Report type for list rendering only.
 */
export const reportMetadataSchema = z.object({
  report_id: z.string(),
  batch_id: z.string(),
  report_type: z.string(),
  data_version: z.string(),
  software_version: z.string(),
  generated_at: z.string(),
});

// ── Calc results (per API_CONTRACT §3 + the BE's CalcResultEntry) ─────────

export const calcResultEntrySchema = z.object({
  kind: z.string(),
  dimensions: z.record(z.string()),
  figure: anyFigureSchema,
  config_version: z.string(),
  software_version: z.string(),
  computed_at: z.string(),
});

// ── Calibration certificate (BE: POST /uploads/calibration-cert) ───────────
//
// Not the upload-ingest shape: `parse_ok` / `rows_parsed` / `row_errors`
// describe a table, and a certificate is a document. What matters here is the
// record written and the findings it closed.

export const uncoveredFindingSchema = z.object({
  issue_id: z.string(),
  batch_id: z.string().nullable(),
  period_start: z.string(),
  period_end: z.string(),
  first_uncovered_day: z.string(),
  uncovered_days: z.number(),
});

export const calibrationCertResponseSchema = z.object({
  upload_id: z.string(),
  calibration_record_id: z.string(),
  equipment_tag: z.string(),
  calibrated_on: z.string(),
  next_due_on: z.string(),
  certificate_s3_key: z.string(),
  resolved_issue_ids: z.array(z.string()),
  // A certificate that covers nothing is still filed (201), so this carries the
  // reason the badge did not move.
  still_open: z.array(uncoveredFindingSchema),
});

// ── Certificate field extraction (BE: POST /uploads/calibration-cert/extract)
//
// A suggestion, not a filing: the endpoint writes nothing, and every field is
// nullable because anything the model could not read — or that failed the
// server's re-check against the roster and the calendar — comes back empty
// rather than guessed.

export const calibrationCertSuggestionSchema = z.object({
  equipment_tag: z.string().nullable(),
  calibrated_on: z.string().nullable(),
  next_due_on: z.string().nullable(),
  certificate_number: z.string().nullable(),
  certificate_issuer: z.string().nullable(),
  note: z.string().nullable(),
});

// ── Equipment roster (BE: GET /equipment) ──────────────────────────────────

export const equipmentRosterItemSchema = z.object({
  tag: z.string(),
  kind: z.string(),
  facility_identifier: z.string().nullable(),
  applicable_standard: z.string().nullable(),
  calibration_interval_days: z.number().nullable(),
  last_calibrated_on: z.string().nullable(),
  next_due_on: z.string().nullable(),
});

// ── Equipment (calibration-due) ────────────────────────────────────────────

export const calibrationDueItemSchema = z.object({
  tag: z.string(),
  kind: z.string(),
  facility_identifier: z.string().nullable(),
  applicable_standard: z.string().nullable(),
  // Null for a `missing` item — an instrument with no calibration record has no
  // due date to count down to (BIO-309).
  last_calibrated_on: z.string().nullable(),
  next_due_on: z.string().nullable(),
  days_until_due: z.number().nullable(),
  status: z.enum(["overdue", "due_soon", "missing"]),
});

// ── Calibration readiness (BE: GET /batches/{id}/calibration-readiness) ─────

export const calibrationReadinessItemSchema = z.object({
  tag: z.string(),
  kind: z.string(),
  facility_identifier: z.string().nullable(),
  applicable_standard: z.string().nullable(),
  status: z.enum(["ok", "due_soon", "overdue", "missing"]),
  must_resolve: z.boolean(),
  last_calibrated_on: z.string().nullable(),
  next_due_on: z.string().nullable(),
  covered_through: z.string().nullable(),
  uncovered_days: z.number(),
  first_uncovered_day: z.string().nullable(),
  days_until_due: z.number().nullable(),
  description: z.string(),
  remediation: z.string(),
  resolved: z.boolean(),
});

export const calibrationReadinessSchema = z.object({
  batch_id: z.string(),
  period_start: z.string(),
  period_end: z.string(),
  instruments_evaluated: z.number(),
  blocking_count: z.number(),
  // The BE's single signal for "the transition gate will refuse this". NOT
  // blocking_count > 0: readiness can be unavailable (no live config, empty
  // registry), which the gate also refuses with no verdicts to count.
  approval_blocked: z.boolean(),
  unavailable_reason: z.string().nullable(),
  items: z.array(calibrationReadinessItemSchema),
});

// ── Uploads (Layer 1) ──────────────────────────────────────────────────────

// Matches the backend `UploadType` (shared/enums.py). `dispenser_records` is
// gated off (F2.6) so the BE never emits it; the six below are all live.
//
// This list must not lag the backend. `GET /uploads` is parsed with
// `z.array(manualUploadRecordSchema)`, and a zod array rejects the WHOLE array if
// any element fails — so one row of a type missing here does not degrade
// gracefully, it throws CONTRACT_VIOLATION and takes out every uploads view at
// once (BIO-314). `uploadTypeSchema.options` is pinned against this set in
// schemas.test.ts so the next backend addition fails a test, not a user's page.
export const uploadTypeSchema = z.enum([
  "pge_statement",
  // Custody statements for injection volume, added for BIO-250. Live on the
  // backend with a registered parser — not gated like `dispenser_records`.
  "pge_injection_volume",
  "dairy_kwh",
  "upgrading_kwh",
  "dairy_manure",
  "calibration_cert",
  "station_allocation",
  // One station's monthly gas bill, the support behind an LCFS ledger row. Like
  // a calibration certificate it is a document rather than a table, filed
  // through its own route with the gas meter number read off the bill.
  "utility_statement",
  // The partner's executed Monthly RNG Confirmation. It restates every month of
  // the contract term, so one document can contradict a period that closed
  // months ago — which is exactly how a wrong June figure survived signature.
  "transaction_confirmation",
]);

// Matches the backend `UploadStatus` (shared/enums.py). NOTE: there is no
// `duplicate` status — a duplicate is signalled by the 409 conflict response on
// POST, handled by the DuplicateUploadPrompt flow, not a stored status.
//
// This list carries the SAME hazard as `uploadTypeSchema` above, and it is the
// one that actually drifted: the backend added `filed` for calibration
// certificates and nothing here or in the tests noticed, so ten filed
// certificates on dev took the whole uploads history down. Both enums are now
// pinned against the backend set in `schemas.test.ts`.
export const uploadStatusSchema = z.enum([
  "received",
  // A document that is complete on arrival — a calibration certificate is filed
  // with its dates in one call, so there is nothing to accept into a batch
  // afterwards. Deliberately neither `accepted` (which would make the
  // certificate evidence for an unrelated completeness finding) nor `parsed`
  // (which feeds the unaccepted-uploads nag for something that can never be
  // accepted).
  "filed",
  "parsed",
  "accepted",
  "rejected",
  "superseded",
]);

// One element of `UploadIngestResponse.row_errors` (uploads/router.py). The BE
// emits a flat list with no severity split — every entry is a blocking error.
export const uploadRowErrorSchema = z.object({
  row_number: z.number(),
  column: z.string().nullable(),
  code: z.string(),
  message: z.string(),
});

// Stored record returned by GET /uploads and GET /uploads/{id}
// (backend `ManualUploadRecord`). This is the persisted row — it carries NO
// parse feedback (row count / errors); that lives only on the POST ingest
// response below. `row_count` is optional so the UI can light up if/when the BE
// enriches this record (see the BE follow-up); it is absent today.
export const manualUploadRecordSchema = z.object({
  id: z.string(),
  upload_type: uploadTypeSchema,
  status: uploadStatusSchema,
  period: z.string(),
  batch_id: z.string().nullable(),
  original_filename: z.string(),
  original_sha256: z.string(),
  content_length: z.number(),
  content_type: z.string().nullable(),
  s3_key: z.string(),
  replaces_upload_id: z.string().nullable(),
  uploaded_by: z.string(),
  uploaded_at: z.string(),
  // Not in the wire today — tolerated for future BE enrichment.
  row_count: z.number().optional(),
});

// Response from POST /uploads (auto-detect), POST /uploads/{type}, and
// POST /uploads/{id}/replace (backend `UploadIngestResponse`). Carries the
// parse outcome the upload portal renders inline; `parse_ok` / `status` gate
// the Accept action. `detected` is true only when the auto-detect route read
// the type + period off the file — optional so response PARSING tolerates an
// older backend; the auto route itself still requires the backend that serves
// POST /uploads, so the backend deploys first.
export const uploadIngestResponseSchema = z.object({
  upload_id: z.string(),
  upload_type: uploadTypeSchema,
  period: z.string(),
  filename: z.string(),
  status: uploadStatusSchema,
  sha256: z.string(),
  content_length: z.number(),
  parse_ok: z.boolean(),
  rows_parsed: z.number(),
  row_errors: z.array(uploadRowErrorSchema),
  detected: z.boolean().optional(),
});

// ── Substitution meter (Layer 2 — F6.6) ────────────────────────────────────

// `band` is left as a plain string (not a narrowed enum) on purpose: the BE
// may add or rename warning bands, and a contract-violation that hides the
// whole meter would be worse than rendering an unknown band with a neutral
// style. The component maps the known set and falls back for anything else.
export const substitutionMeterSchema = z.object({
  batch_id: z.string(),
  substituted_days: z.number(),
  total_days: z.number(),
  fraction: z.string(),
  max_consecutive_days: z.number(),
  limit_fraction: z.string(),
  limit_consecutive_days: z.number(),
  utilization: z.string(),
  band: z.string(),
  over_threshold: z.boolean(),
});

// ── Config (Layer 0 — customer profile versioning) ─────────────────────────

export const emtsIdentifiersSchema = z.object({
  company_name: z.string(),
  cid: z.string(),
  facility_name: z.string(),
  fid: z.string(),
  facility_type: z.string(),
  feedstock_code: z.string(),
  fuel_code: z.string(),
  biogas_feedstock_code_1: z.string(),
});

export const conversionFactorsSchema = z.object({
  lhv_hhv_ratio: z.string(),
  btu_lhv_per_rin: z.string(),
  mmbtu_per_gj: z.string(),
  btu_per_scf_hhv: z.string(),
  // RF Gallons only; absent means lhv_hhv_ratio. Declared so a Config-page save,
  // which PATCHes the whole parsed content, does not strip it.
  rin_lhv_hhv_ratio: z.string().optional(),
});

export const calcMethodsSchema = z.object({
  token_mmbtu: z.enum(["energy_totalizer", "scf_ch4_btu"]),
  source_granularity: z.enum(["daily", "hourly_summed"]),
});

export const substitutionThresholdsSchema = z.object({
  max_consecutive_days: z.number(),
  max_fraction: z.string(),
});

export const reportingEntitySchema = z.object({
  role: z.enum(["rfs", "lcfs_regulated_party", "biofuel_facility", "biogas_producer"]),
  name: z.string(),
  identifier: z.string(),
});

export const fuelPathwayCodeSchema = z.object({
  fpc_code: z.string(),
  dairy_identifier: z.string(),
  dairy_name: z.string(),
  status: z.enum(["permanent", "temporary"]),
  certified_ci_gco2e_per_mj: z.string().nullable(),
  effective_date: z.string(),
});

// `.passthrough()` preserves backend-only content keys (lcfs, data_quality, …)
// that the FE doesn't surface, so an in-place edit round-trips them untouched
// rather than resetting them to defaults on PATCH.
export const customerProfileSchema = z
  .object({
    profile_key: z.string(),
    timezone: z.string(),
    emts: emtsIdentifiersSchema,
    conversion_factors: conversionFactorsSchema,
    token_mcv_mmbtu: z.string(),
    rin_mcv_mmbtu: z.string(),
    calc_methods: calcMethodsSchema,
    substitution: substitutionThresholdsSchema,
    reporting_entities: z.array(reportingEntitySchema),
    fuel_pathway_codes: z.array(fuelPathwayCodeSchema),
  })
  .passthrough();

export const batchConfigSchema = z.object({
  token_mcv_mmbtu: z.string(),
  rin_mcv_mmbtu: z.string(),
  conversion_factors: conversionFactorsSchema,
  calc_methods: calcMethodsSchema,
});

export const configVersionSummarySchema = z.object({
  id: z.string(),
  profile_key: z.string(),
  // effective_from/to + label are vestiges of the retired versioned-config
  // model (BIO-227); the single live config the backend now returns omits them,
  // so all three are optional here rather than required-but-null.
  effective_from: z.string().nullable().optional(),
  effective_to: z.string().nullable().optional(),
  label: z.string().nullable().optional(),
});

export const configVersionSchema = configVersionSummarySchema.extend({
  content: customerProfileSchema,
});

export const configFieldChangeSchema = z.object({
  path: z.string(),
  change: z.string(),
  from_value: z.unknown().optional(),
  to_value: z.unknown().optional(),
});

export const configDiffSchema = z.object({
  from_version_id: z.string(),
  to_version_id: z.string(),
  changes: z.array(configFieldChangeSchema),
});

// ── LCFS: FSE registry ─────────────────────────────────────────────────────

export const fseRecordSchema = z.object({
  fse_id: z.string(),
  station_name: z.string(),
  lff_id: z.string().nullable(),
  trillium_fse_id: z.string().nullable(),
  dispenser: z.string(),
  utility: z.string().nullable(),
  meter_number: z.string().nullable(),
  address: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  postal_code: z.string().nullable(),
  address_normalized: z.string().nullable(),
  address_review: z.string().nullable(),
});

// ── LCFS: allocation ledger + lot inventory ────────────────────────────────

const mmbtuFigureSchema = figureOf(z.literal("mmbtu"));

export const stationClosureStateSchema = z.enum(["ok", "residual", "queue_exhausted"]);

export const allocationClosureSchema = z.object({
  station_closure: stationClosureStateSchema,
  target_closure: z.enum(["ok", "mismatch"]),
  unexplained_stations: z.number(),
  residual_total: mmbtuFigureSchema,
});

export const allocationLedgerRowSchema = z.object({
  dairy_id: z.string(),
  dairy_name: z.string(),
  vintage_month: z.string(),
  age_periods: z.number(),
  mmbtu: mmbtuFigureSchema,
  fpc_code: z.string(),
});

export const allocationStationGroupSchema = z.object({
  station: z.string(),
  aemetis_fse_id: z.string(),
  feed_order: z.number(),
  capacity: mmbtuFigureSchema,
  residual: mmbtuFigureSchema,
  rows: z.array(allocationLedgerRowSchema),
});

export const allocationPeriodSchema = z.object({
  period: z.string(),
  allocated: mmbtuFigureSchema,
  capacity: mmbtuFigureSchema,
  banked: mmbtuFigureSchema,
  queue_exhausted: z.boolean(),
  row_count: z.number(),
  lots_touched: z.number(),
  closure: allocationClosureSchema,
  stations: z.array(allocationStationGroupSchema),
  available_periods: z.array(z.string()),
  computed_at: z.string(),
});

export const lotRowSchema = z.object({
  dairy_id: z.string(),
  dairy_name: z.string(),
  fpc_code: z.string(),
  vintage_month: z.string(),
  age_periods: z.number(),
  produced: mmbtuFigureSchema,
  opening: mmbtuFigureSchema,
  consumed: mmbtuFigureSchema,
  remaining: mmbtuFigureSchema,
});

export const lotInventorySchema = z.object({
  period: z.string(),
  items: z.array(lotRowSchema),
  banked_total: mmbtuFigureSchema,
  oldest_age_periods: z.number(),
  oldest_banked_vintage: z.string().nullable(),
  available_periods: z.array(z.string()),
  computed_at: z.string(),
});

// ── LCFS: dairy registry ───────────────────────────────────────────────────

export const pathwayStatusSchema = z.enum(["certified", "temporary", "pending"]);

export const dairyRecordSchema = z.object({
  dairy_id: z.string(),
  /**
   * The canonical registry name, resolved by the backend from
   * `public.facilities`. Not the workbook's spelling — those genuinely differ,
   * and two of them are a different name for the same farm rather than a
   * misspelling.
   */
  name: z.string(),
  /**
   * What the customer's own sheet calls this dairy, kept so a verifier can
   * reconcile against it. Optional: a backend predating BIO-380 does not send
   * it, and the screens fall back to `name`.
   */
  workbook_name: z.string().optional(),
  /** `null` where the dairy has no facility at all, so no telemetry. */
  facility_identifier: z.string().nullable().optional(),
  roster_position: z.number(),
  fpc_code: z.string().nullable(),
  pathway_status: pathwayStatusSchema,
  certified_ci_gco2e_per_mj: z.string().nullable(),
  pathway_note: z.string().nullable(),
  rin_eligibility_date: z.string().nullable(),
  first_production_month: z.string().nullable(),
});

// ── LCFS: the allocation's stored inputs ────────────────────────────────────
//
// The engine runs in the browser over these three lists plus the dairy roster.
// Figures stay decimal strings all the way through — `allocationView.ts` hands
// them straight to Decimal and never to `Number()`.

export const dairyProductionRowSchema = z.object({
  dairy_id: z.string(),
  period: z.string(),
  mmbtu: z.string(),
  /**
   * Whether this figure was measured at the dairy's own custody meter or
   * transcribed from the customer's workbook (BIO-381).
   *
   * Optional, and defaulted to `workbook`: a backend predating the provenance
   * columns does not send it, and `workbook` is what every figure was before
   * they existed — so the default is the truth rather than a placeholder.
   */
  source: z.enum(["telemetry", "workbook"]).optional().default("workbook"),
  /**
   * How many of the month's days leaned on a tier below the custody meter — the
   * GC outlet flow or the plant's own consolidated record. 0 for a fully
   * metered month, and always 0 on a transcribed figure.
   */
  substituted_days: z.number().optional().default(0),
});

export const stationCapacityRowSchema = z.object({
  fse_id: z.string(),
  period: z.string(),
  mmbtu: z.string(),
  /** Dispensing-feed arrival order — the order the allocation consumes them. */
  feed_order: z.number(),
});

// ── LCFS: loading a period from the partner's allocation workbook ──────────
//
// The preview response. Parsed strictly like every other figure on the wire —
// `mmbtu` stays a decimal string and is never `Number()`d, because these become
// the claimed capacities a filing is built from.

const resolvedViaSchema = z.enum(["alias", "station_name", "operator", "created"]);

const previewSiteSchema = z.object({
  source_name: z.string(),
  mmbtu: z.string(),
  feed_order: z.number(),
  // Null exactly when the site did not resolve, which is what blocks the load.
  fse_id: z.string().nullable(),
  station_name: z.string().nullable(),
  via: resolvedViaSchema.nullable(),
});

const capacityChangeSchema = z.object({
  fse_id: z.string(),
  station_name: z.string().nullable(),
  before: z.string().nullable(),
  after: z.string().nullable(),
});

const workbookMonthSchema = z.object({
  sheet: z.string(),
  period: z.string().nullable(),
  total_mmbtu: z.string().nullable(),
  sites: z.number(),
  loaded: z.boolean(),
  error: z.string().nullable(),
});

export const allocationPreviewSchema = z.object({
  period: z.string(),
  sheet: z.string(),
  total_mmbtu: z.string(),
  dispenser_totals: z.record(z.string(), z.string()),
  sites: z.array(previewSiteSchema),
  unresolved: z.array(previewSiteSchema),
  changes: z.array(capacityChangeSchema),
  ready: z.boolean(),
  // Optional rather than defaulted: `.default()` splits zod's input and output
  // types and breaks `apiFetch`'s inference. Absent only from a backend that
  // predates multi-month loading.
  months: z.array(workbookMonthSchema).optional(),
});

// ── LCFS: the quarter's partner throughput, beside the ledger ─────────────

export const lcfsThroughputRowSchema = z.object({
  station: z.string(),
  partner_fse_id: z.string(),
  fse_id: z.string().nullable(),
  station_name: z.string().nullable(),
  total_throughput_therms: z.string(),
  partner_customer_therms: z.string().nullable(),
  ledger_customer_therms: z.string().nullable(),
});

export const lcfsThroughputPreviewSchema = z.object({
  year: z.number(),
  quarter: z.number(),
  sheet: z.string(),
  rows: z.array(lcfsThroughputRowSchema),
  missing: z.array(
    z.object({
      fse_id: z.string(),
      station_name: z.string(),
      ledger_customer_therms: z.string(),
    }),
  ),
  ledger_total_therms: z.string(),
  partner_customer_total_therms: z.string().nullable(),
  shortfalls: z.array(z.object({ period: z.string(), fse_id: z.string(), mmbtu: z.string() })),
  stations: z.array(z.object({ fse_id: z.string(), station_name: z.string() })),
});

// ── LCFS: a period's utility-statement support ────────────────────────────
//
// The verdict is derived server-side on every read, so there is no run id and no
// polling — see `lcfs/validation.py` on why this one is synchronous while batch
// validation is a 202.

export const lcfsFindingCodeSchema = z.enum([
  "statement_missing",
  "station_unidentified",
  "meter_mismatch",
  "confirmation_mismatch",
]);

export const lcfsPeriodFindingSchema = z.object({
  code: lcfsFindingCodeSchema,
  message: z.string(),
  /** Null for an unidentified station — the case of having no meter. */
  gas_meter_number: z.string().nullable(),
  /** Every station the finding covers; a shared meter names more than one. */
  fse_ids: z.array(z.string()),
  station_names: z.array(z.string()),
});

export const lcfsPeriodValidationSchema = z.object({
  period: z.string(),
  /**
   * `not_required` is not a pass: it means the period predates the first month
   * the partner sent support, so there is nothing to grade. It must never render
   * as green.
   */
  status: z.enum(["passed", "failed", "not_required"]),
  required_statements: z.number(),
  filed_statements: z.number(),
  findings: z.array(lcfsPeriodFindingSchema),
});

export const utilityStatementRecordSchema = z.object({
  id: z.string(),
  upload_id: z.string(),
  period: z.string(),
  fse_id: z.string(),
  gas_meter_number: z.string(),
  utility: z.string().nullable(),
  account_number: z.string().nullable(),
  /** The bill's own window. Shown because it is usually NOT the month; never graded. */
  statement_start: z.string().nullable(),
  statement_end: z.string().nullable(),
  filed_at: z.string(),
  original_filename: z.string(),
});

export const transactionConfirmationRecordSchema = z.object({
  id: z.string(),
  upload_id: z.string(),
  tc_ref: z.string().nullable(),
  /** The signature date — the ONLY field that safely orders revisions. */
  executed_on: z.string(),
  /** As printed on the document. Display only: not monotonic across revisions. */
  confirmation_date: z.string().nullable(),
  filed_at: z.string(),
  original_filename: z.string(),
  months_stated: z.number(),
});

export const confirmationDisagreementSchema = z.object({
  period: z.string(),
  stated_mmbtu: z.string(),
  our_mmbtu: z.string(),
});

export const confirmationPreviewSchema = z.object({
  tc_ref: z.string().nullable(),
  confirmation_date: z.string().nullable(),
  /** Null when the copy carries no completion stamp — then the operator types it. */
  executed_on: z.string().nullable(),
  months: z.array(
    z.object({
      period: z.string(),
      minimum_mmbtu: z.string(),
      spot_mmbtu: z.string(),
      contract_mmbtu: z.string(),
    }),
  ),
});

export const filedConfirmationSchema = z.object({
  confirmation_id: z.string(),
  upload_id: z.string(),
  tc_ref: z.string().nullable(),
  /** The date used to order this revision — typed, or read off the document. */
  executed_on: z.string(),
  months_stated: z.number(),
  /** The months this document contradicts — the answer the operator needs. */
  disagreements: z.array(confirmationDisagreementSchema),
  confirmation_s3_key: z.string(),
});

export const filedUtilityStatementSchema = z.object({
  statement_id: z.string(),
  upload_id: z.string(),
  period: z.string(),
  fse_id: z.string(),
  station_name: z.string(),
  /** Echoed back: it is the field the whole control exists to get right. */
  gas_meter_number: z.string(),
  statement_s3_key: z.string(),
});

// ── List envelope + error envelope ─────────────────────────────────────────

type ListShape<T> = { items: T[]; next_cursor: string | null };

/**
 * Accepts both list shapes the BE may emit and normalizes to one:
 *   - Envelope: `{ items: [...], next_cursor: "..." | null }` (MSW + future BE)
 *   - Bare array: `[...]` with the cursor in the `X-Next-Cursor` response
 *     header (current FastAPI implementation as of 2026-06-03)
 *
 * Output is always `{ items, next_cursor: string | null }`. The bare-array
 * path currently loses the cursor — when pagination matters, apiFetch can
 * be taught to read `X-Next-Cursor` from the response and merge it in.
 *
 * The explicit return-type annotation works around zod's preprocess
 * losing the inner schema's inferred type when nested.
 */
export function listResponseSchema<T extends z.ZodTypeAny>(
  itemSchema: T,
): z.ZodType<ListShape<z.infer<T>>> {
  return z.preprocess(
    (val) => (Array.isArray(val) ? { items: val, next_cursor: null } : val),
    z.object({
      items: z.array(itemSchema),
      next_cursor: z
        .string()
        .nullish()
        .transform((v) => v ?? null),
    }),
  ) as unknown as z.ZodType<ListShape<z.infer<T>>>;
}

export const apiErrorEnvelopeSchema = z.object({
  code: z.string(),
  message: z.string(),
  request_id: z.string(),
  // The backend serializes errors with `exclude_none=False`, so absent details
  // arrive as an explicit `null` (not omitted). Accept both null and undefined —
  // otherwise EVERY error without details fails envelope parsing and the real
  // message is masked by a generic MALFORMED_ERROR. Normalize null → undefined
  // so the parsed type stays assignable to ApiErrorEnvelope.
  details: z
    .record(z.unknown())
    .nullish()
    .transform((d) => d ?? undefined),
});

// ── Browser auth (/api/v2/auth) ────────────────────────────────────────────
//
// A different mounted sub-app from the compliance API, with a different error
// shape: RFC 9457 Problem Details rather than the flat envelope above. Both are
// normalised to ApiError in `auth/session.ts`.

/**
 * RFC 9457 Problem Details. `code` is the machine-readable half the UI branches
 * on; `type` is its documentation URI and `title` the stable summary of the
 * problem *type*, so per-occurrence text lives in `detail`.
 */
export const problemDetailsSchema = z.object({
  type: z.string(),
  title: z.string(),
  status: z.number(),
  code: z.string(),
  detail: z.string(),
});

export const sessionIdentitySchema = z.object({
  id: z.string(),
  email: z.string(),
  displayName: z
    .string()
    .nullish()
    .transform((v) => v ?? null),
  status: z.string(),
});

/**
 * The answer from `/session`, `/refresh`, and a completed `/login` alike — one
 * shape so there is a single code path for "what is my session now", whichever
 * call produced it.
 */
export const sessionResponseSchema = z.object({
  state: z.enum(["authenticated", "refreshable", "anonymous"]),
  identity: sessionIdentitySchema.nullish().transform((v) => v ?? null),
  csrfToken: z.string(),
  accessExpiresAt: z
    .string()
    .nullish()
    .transform((v) => v ?? null),
  // Reported by every session answer - bootstrap, login and refresh alike - so a
  // null means the marker was cleared rather than "this endpoint is silent". Still
  // nullish here: the coercion to null is what keeps the parsed type
  // non-optional, and an older API that omits the field reads as no marker, which
  // is the safe direction (show the form).
  lastProvider: z
    .enum(["microsoft", "cognito"])
    .nullish()
    .transform((v) => v ?? null),
});

/**
 * The *other* thing `POST /login` can answer with: a first sign-in that must
 * replace its temporary password before any session exists.
 *
 * This is the documented onboarding path, not an edge case — the user-management
 * runbook tells operators to create accounts *without* `--permanent`, so every
 * newly onboarded user arrives here on their first sign-in.
 *
 * `challengeSession` is short-lived opaque provider state. Do not log or persist
 * it; hold it in memory only for the immediately following request.
 */
export const newPasswordChallengeSchema = z.object({
  challengeName: z.literal("NEW_PASSWORD_REQUIRED"),
  challengeSession: z.string().min(1),
  username: z.string(),
  requiredAttributes: z.array(z.string()).default([]),
  csrfToken: z.string(),
});

/**
 * What `POST /login` actually returns. Discriminated on `challengeName`, which
 * is present on exactly one of the two.
 */
export const loginResponseSchema = z.union([newPasswordChallengeSchema, sessionResponseSchema]);

// ── Data visualization (DATA_VISUALIZATION_*_PLAN) ──────────────────────────
//
// MSW-first surface (see mocks/handlers.ts) — same wire shapes the BE will
// serve. `points[].value` uses the SAME canonical decimal-string regex as
// MeasuredValue (API_CONTRACT §3); a non-canonical figure is a contract
// violation, not a render-time NaN.

const decimalStringSchema = z.string().regex(/^-?\d+(\.\d+)?$/, "not a canonical decimal string");

export const dataSourceScopeSchema = z.enum(["hub", "dairy"]);

export const dataSourceGroupSchema = z.enum(["hub_inlet", "hub_outlet", "hub_custody", "dairy"]);

export const sourceCatalogEntrySchema = z.object({
  role: z.string(),
  // Curated by the backend. The picker used to title-case the role key, which is
  // how the XRC2 plant meter came to be labelled "PGE Outlet Volume".
  label: z.string(),
  group: dataSourceGroupSchema,
  group_label: z.string(),
  scope: dataSourceScopeSchema,
  kind: z.string(),
  unit: figureBasisSchema,
  description: z.string(),
  requires_facility: z.boolean(),
  // True for raw-totalizer roles (hourly series available); report-view +
  // utility roles are daily-only. Drives the granularity toggle.
  hourly_capable: z.boolean(),
});

export const facilityEntrySchema = z.object({
  identifier: z.string(),
  display_name: z.string(),
  scope: dataSourceScopeSchema,
});

export const sourcesCatalogResponseSchema = z.object({
  sources: z.array(sourceCatalogEntrySchema),
  facilities: z.array(facilityEntrySchema),
  upload_types: z.array(z.string()),
});

export const seriesPointSchema = z.object({
  // Bucket start: a tz-aware ISO timestamp — PT gas-day midnight (daily) or the
  // hour (hourly). Replaces the old `day` field (now unified across granularities).
  bucket_start: z.string(),
  value: decimalStringSchema,
  // Source samples in the bucket — hourly totalizer roles only; null otherwise.
  // A data-completeness signal (~3600/hr at 1 Hz).
  n_readings: z.number().nullable().optional(),
});

export const seriesResponseSchema = z.object({
  role: z.string(),
  facility_identifier: z.string(),
  // The DISPLAY unit the figures are in — MSCF for every volume.
  unit: figureBasisSchema,
  // The unit the backend reader emits, and the unit corrections are stored in.
  native_unit: figureBasisSchema,
  // Multiply a displayed figure by this to recover the native one. Load-bearing
  // for the correction picker: a correction proposed off a displayed MSCF figure
  // must be stored in SCF, or the applier and validation read it 1000x small.
  native_divisor: decimalStringSchema,
  granularity: z.string(),
  period_start: z.string(),
  period_end: z.string(),
  points: z.array(seriesPointSchema),
  total: anyFigureSchema,
});

export const uploadRowEntrySchema = z.object({
  row_number: z.number(),
  data: z.record(z.unknown()),
});

export const uploadRowsResponseSchema = z.object({
  upload_id: z.string(),
  total_rows: z.number(),
  limit: z.number(),
  offset: z.number(),
  rows: z.array(uploadRowEntrySchema),
});

// ── Notifications (Layer 6) ─────────────────────────────────────────────────
//
// `severity` is left as a plain string (not a narrowed enum): the BE may add
// topic severities, and a contract violation that hides the whole list would be
// worse than rendering an unknown value with a neutral style. The components map
// the known set and fall back otherwise.

export const topicInfoSchema = z.object({
  key: z.string(),
  name: z.string(),
  severity: z.string(),
  default_roles: z.array(z.string()),
});

export const subscriptionStateSchema = z.object({
  topic_key: z.string(),
  enabled: z.boolean(),
  is_override: z.boolean(),
});

// ── Backfill (Layer 5) ──────────────────────────────────────────────────────

export const backfillStatusSchema = z.object({
  batch_id: z.string(),
  is_backfill: z.boolean(),
  certified: z.boolean(),
});

// ── Workbook comparison (Layer 6) ───────────────────────────────────────────
//
// platform_value / workbook_value / variance_pct are decimal-as-string (§3).
// Kept as plain strings (not the canonical-decimal regex) so a BE-emitted edge
// value never surfaces as a contract violation that blanks the whole table.

export const workbookVarianceSchema = z.object({
  cell_ref: z.string(),
  metric: z.string(),
  platform_value: z.string(),
  workbook_value: z.string(),
  variance_pct: z.string(),
  within_tolerance: z.boolean(),
  agreed_variance: z.boolean(),
  note: z.string().nullable(),
});

// ── Validation run (Layer 1) ────────────────────────────────────────────────

export const validationRunResultSchema = z.object({
  batch_id: z.string(),
  findings: z.number(),
  inserted: z.number(),
  series_loaded: z.number(),
});

// Validation now runs in the background (the DUT fan-out is slow / can be
// unreachable), so POST /validate returns a 202 ack — not the run counts.
export const validationStartedSchema = z.object({
  batch_id: z.string(),
  status: z.string(),
});

export const validationRunSummarySchema = z.object({
  id: z.string(),
  status: z.enum(["running", "completed", "failed"]),
  trigger: z.enum(["manual", "sweep"]),
  started_at: z.string(),
  finished_at: z.string().nullable(),
  // Null until the run's dataset load resolved them (and on a run that died there).
  data_version: z.string().nullable(),
  config_digest: z.string().nullable(),
  findings: z.number().nullable(),
  inserted: z.number().nullable(),
  // Findings the run auto-resolved because they no longer reproduce.
  resolved: z.number().nullable(),
  series_loaded: z.number().nullable(),
  last_error: z.string().nullable(),
  // Coarse label for where a run has got to, from the backend's RunStage
  // vocabulary. Only meaningful while `status === "running"` — a finished run
  // keeps whatever it last reported. Optional so this bundle still parses a
  // backend that predates it (see lib/runStages.ts for the copy mapping).
  stage: z.string().nullable().optional(),
});

// Whether the persisted findings still describe the batch's current data.
// `stale` is the backend's single verdict — the same value the transition gate
// refuses the forward edges with, from the same service — so the UI renders it
// and never
// re-derives it (the `approval_blocked` precedent).
export const validationStatusSchema = z.object({
  batch_id: z.string(),
  // Most recent run of any status: drives "last validated" and the spinner.
  last_run: validationRunSummarySchema.nullable(),
  // Most recent COMPLETED run — the one whose findings are on screen and the
  // only one `stale` can be about. Differs from `last_run` while a re-run is in
  // flight or after one failed.
  last_completed_run: validationRunSummarySchema.nullable(),
  current_data_version: z.string(),
  stale: z.boolean(),
  stale_reasons: z.array(z.string()),
  //: Whole gas-days that arrived inside the period after the graded run
  //: finished. Non-zero iff `period_advanced` is among the reasons. Optional so
  //: this bundle still parses a backend that predates the axis.
  new_data_days: z.number().optional(),
  never_validated: z.boolean(),
  running: z.boolean(),
  // The last run still says running but nothing is working on it — a deploy
  // killed the background task. Distinct from failed: the cause is not the
  // batch's data. Optional for the same deploy-order reason as `stage`.
  abandoned: z.boolean().optional(),
});

// ── Upload accept (Layer 1) ─────────────────────────────────────────────────

export const uploadAcceptResponseSchema = z.object({
  upload_id: z.string(),
  batch_id: z.string(),
  old_data_version: z.string(),
  new_data_version: z.string(),
});

// ── Re-exports for non-zod consumers ───────────────────────────────────────

export { anyFigureSchema };
